import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { UsersThree, Globe, Plus, Detective, MapPin, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import MemoryLanePromo from "@/components/MemoryLanePromo";
import SampleLinks from "@/components/SampleLinks";
import AlmaMaterPromo from "@/components/AlmaMaterPromo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EDITION, isIndia } from "@/lib/edition";
import { useLanguage } from "@/context/LanguageContext";

function GroupCard({ g, showInstitution }) {
  return (
    <Link to={`/groups/${g.id}`} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md" data-testid={`group-card-${g.id}`}>
      <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-green-100 text-green-700"><UsersThree size={22} weight="duotone" /></span>
      <h3 className="mt-3 font-heading text-lg font-medium leading-tight">{g.name}</h3>
      {showInstitution && g.institution_name && (
        <p className="mt-1 flex items-center gap-1 text-xs text-orange-600"><MapPin size={12} /> {g.institution_name}</p>
      )}
      {g.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{g.description}</p>}
      <div className="mt-3 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><UsersThree size={13} /> {g.member_count} member{g.member_count === 1 ? "" : "s"}</p>
        <span className="flex items-center gap-1 text-xs font-medium text-green-700">Join <ArrowRight size={12} /></span>
      </div>
    </Link>
  );
}

export default function AlumniGroups() {
  useSeo("Alumni Groups", "Discover public alumni groups and communities by country.");
  const { t } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [groups, setGroups] = useState(null);
  const [byCountry, setByCountry] = useState(null);

  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/groups/by-country").then((r) => setByCountry(r.data || [])).catch(() => setByCountry([])); }, []);
  useEffect(() => {
    if (!country) { setGroups(null); return; }
    api.get("/public/groups", { params: { country } }).then((r) => setGroups(r.data)).catch(() => setGroups([]));
  }, [country]);

  return (
    <div className="mx-auto max-w-6xl px-5 py-14">
      <SampleLinks className="mb-6" />
      <AlmaMaterPromo className="mb-6" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <p className="overline text-secondary mb-3">{t("Communities")}</p>
          <h1 className="font-heading text-4xl font-light leading-[1.08] tracking-tight">{t("Reconnect with the people who")} <span className="italic font-medium text-secondary">{t("shaped your journey.")}</span></h1>
          <p className="mt-3 text-muted-foreground">{t("Record your entire educational history, rejoin your class and batch, preserve memories, and build communities that last a lifetime — across the world.")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/find"><Button variant="outline" className="rounded-full gap-1.5" data-testid="find-colleague-cta"><Detective size={16} /> {t("Look for a Lost Colleague")}</Button></Link>
          <Link to="/groups"><Button className="rounded-full gap-1.5" data-testid="create-group-cta"><Plus size={16} /> {t("Create a group")}</Button></Link>
        </div>
      </div>

      {/* Article teaser */}
      <div className="mt-8 rounded-2xl border border-border/60 bg-card p-6 sm:p-8" data-testid="groups-article-teaser">
        <p className="text-sm text-muted-foreground">{t("Reconnect with the people who shared and shaped your journey.")}</p>
        <p className="mt-4 leading-relaxed text-foreground/90">{t("School and college friendships may begin in the classroom, but the connections can last a lifetime.")} {EDITION.brand} {t("brings former students, classmates, teachers, and colleagues together in communities built around shared experiences, memories, and milestones.")}</p>
        <p className="mt-4 leading-relaxed text-foreground/90">{t("Find your school, college, university, batch, class, department, or graduating year and reconnect with the people you once knew. Join an existing alumni group or create one for your class, workplace, neighborhood, or community.")}</p>
        <Link to="/articles/bringing-long-lost-friends-back-together" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline" data-testid="groups-article-more">{t("Read more")} <ArrowRight size={14} /></Link>
      </div>

      {/* Invitation banner */}
      <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-6 sm:flex sm:items-center sm:justify-between" data-testid="groups-invite-banner">
        <div className="max-w-xl">
          <h2 className="font-heading text-lg font-medium text-orange-950">{t("Your people are already here")}</h2>
          <p className="mt-1 text-sm text-orange-900/80">{isIndia ? t("Explore alumni communities across India below, or post a request to find a lost classmate or colleague.") : t("Join thousands of alumni rebuilding their circles. Pick your country below to explore communities, or post a request to find a lost classmate or colleague.")}</p>
          <Link to="/articles/find-a-lost-colleague" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:underline" data-testid="find-lost-article-link">Find your Long Lost Friend or Colleague <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 sm:mt-0 sm:shrink-0">
          <Link to="/find"><Button variant="outline" className="rounded-full gap-1.5 border-orange-300 bg-white text-orange-700 hover:bg-orange-100" data-testid="invite-find-cta"><Detective size={16} /> Find someone</Button></Link>
          <Link to="/groups"><Button className="rounded-full gap-1.5 bg-orange-600 text-white hover:bg-orange-700" data-testid="invite-join-cta"><UsersThree size={16} /> Join a group</Button></Link>
        </div>
      </div>

      <MemoryLanePromo className="mt-8" />

      {!isIndia && (
      <div className="mt-8 max-w-xs">
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="rounded-full" data-testid="group-country-select"><SelectValue placeholder="Filter by country" /></SelectTrigger>
          <SelectContent>{countries.map((c) => <SelectItem key={c.country} value={c.country}>{c.country}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      )}

      {country ? (
        groups === null ? <p className="mt-10 text-muted-foreground">Loading…</p>
          : groups.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-border/60 bg-muted/30 p-10 text-center">
              <p className="text-muted-foreground">No public groups in {country} yet.</p>
              <Link to="/groups"><Button variant="secondary" className="mt-4 rounded-full gap-1.5"><Plus size={16} /> Be the first — create one</Button></Link>
            </div>
          ) : (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {groups.map((g) => <GroupCard key={g.id} g={g} showInstitution />)}
            </div>
          )
      ) : byCountry === null ? <p className="mt-10 text-muted-foreground">Loading communities…</p>
        : byCountry.length === 0 ? (
          <div className="mt-16 grid place-items-center rounded-2xl border border-dashed border-border/70 py-20 text-center">
            <Globe size={40} weight="duotone" className="text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">No public groups yet. Be the first to start one.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-12" data-testid="groups-by-country">
            {byCountry.map((bucket) => (
              <section key={bucket.country} data-testid={`country-section-${bucket.country}`}>
                <div className="flex items-end justify-between border-b border-border/50 pb-3">
                  <h2 className="flex items-center gap-2 font-heading text-2xl font-light">
                    <Globe size={22} weight="duotone" className="text-secondary" /> {bucket.country}
                  </h2>
                  <button onClick={() => setCountry(bucket.country)} className="text-sm font-medium text-primary hover:underline" data-testid={`see-all-${bucket.country}`}>
                    See all {bucket.total} →
                  </button>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {bucket.groups.map((g) => <GroupCard key={g.id} g={g} showInstitution />)}
                </div>
              </section>
            ))}
          </div>
        )}
    </div>
  );
}
